import { useCallback, useMemo, useState } from 'react';
import {
  Grid,
  Divider,
  PanelContent,
  SearchableTextField,
  FilterButton,
  Tooltip,
  useTranslations,
  usePopper,
  ActionButton,
  ShareIcon,
  ErrorIcon,
  DeleteIcon,
  useMapKeyValueExtractor,
  useHeroSnackbar,
  PromptModal,
  ISearchableTextFieldProps,
  IFilterButtonProps,
  encryptSymmetric,
  Button,
  PlusIcon,
  Box,
  MoveIcon,
  MoveToEmployeeIcon,
  MoveToGroupIcon,
  Typography,
  SeverityEnum,
} from '@uniqkey-frontend/shared-app';
import { Ownership } from '@uniqkey-backend-organization-web/api-client';
import useOrganizationSecureNotesTable, {
  IOrganizationSecureNotesTableRow,
} from '../../../../hooks/tables/useOrganizationSecureNotesTable';
import OrganizationSecureNotesTable
  from '../../../../components/tables/OrganizationSecureNotesTable';
import OrganizationSecureNotesTabFilter, {
  IOrganizationSecureNotesTabFilterSubmitResult,
} from './components/OrganizationSecureNotesTabFilter';
import { useDeleteVaults, useDetachVaults } from '../../../../hooks/reactQuery';
import VaultTypeEnum from '../../../../enums/VaultTypeEnum';
import ShareVaultsToGroupModal from '../../../../components/ShareVaultsToGroupModal';
import { logException } from '../../../../services/sentryService';
import CreateSecureNoteModal, {
  ICreateSecureNoteReturnValue,
} from '../../../../components/CreateSecureNoteModal';
import { useCompanionApplicationContext } from '../../../../contexts/CompanionApplicationContext';
import useVaultsAPI from '../../../../hooks/useVaultsAPI';
import { getActiveOrganizationId } from '../../../../services/organizationService';
import { useTrustedPortalStore } from '../../../../modules/TrustedPortalModule/store';
import MoveVaultsToEmployeeModal from '../../../../components/MoveVaultsToEmployeeModal';
import MoveVaultsToGroupModal from '../../../../components/MoveVaultsToGroupModal';
import { getTranslationKeyByError } from '../../../../helpers/errorService';

const OrganizationSecureNotesTab = () => {
  const [isShareSecureNotesModalOpen, setIsShareSecureNotesModalOpen] = useState(false);
  const [isDetachSecureNotesModalOpen, setIsDetachSecureNotesModalOpen] = useState(false);
  const [isDeleteSecureNotesModalOpen, setIsDeleteSecureNotesModalOpen] = useState(false);
  const [isCreateSecureNoteModalOpen, setIsCreateSecureNoteModalOpen] = useState(false);
  const [isCreateSecureNoteLoading, setIsCreateSecureNoteLoading] = useState(false);
  const [isMoveToPrivateSecureNotesModalOpen, setIsMoveToPrivateSecureNotesModalOpen] = useState(
    false,
  );
  const [isMoveToPrivateSecureNotesLoading, setIsMoveToPrivateSecureNotesLoading] = useState(false);
  const [isMoveToEmployeeSecureNotesModalOpen, setIsMoveToEmployeeSecureNotesModalOpen] = useState(
    false,
  );
  const [isMoveToGroupSecureNotesModalOpen, setIsMoveToGroupSecureNotesModalOpen] = useState(
    false,
  );

  const { t } = useTranslations();
  const {
    isOpen: isFilterOpen,
    anchorEl: filterAnchorEl,
    setPopperIsOpen: setIsFilterOpen,
  } = usePopper();
  const { showError, showSuccess, showWarning } = useHeroSnackbar();
  const { symmetricKey } = useCompanionApplicationContext();
  const { createVaultSecureNote, moveVaultsToPrivateKeychain } = useVaultsAPI();
  const {
    selectedSecureNotes,
    searchQuery,
    setSearchQuery,
    filterValues,
    setFilterValues,
    isFilterActive,
    numberOfActiveFilters,
    resetActivePage,
    resetSelectedRows,
    ...restTableProps
  } = useOrganizationSecureNotesTable({
    noDataMessageKey: 'organizationSecureNotesTab.noData',
  });
  const {
    values: selectedSecureNotesAsObjects, keys: selectedSecureNotesIds,
  } = useMapKeyValueExtractor<IOrganizationSecureNotesTableRow>(selectedSecureNotes);
  const activeOrganizationId = getActiveOrganizationId();
  const isTrustedPortalEnabled = useTrustedPortalStore.useIsEnabledByOrganizationId()[
    activeOrganizationId!
  ] ?? false;
  const {
    mutate: mutateDetachSecureNote, isLoading: isDetachSecureNotesLoading,
  } = useDetachVaults();
  const {
    mutate: mutateDeleteSecureNote, isLoading: isDeleteSecureNotesLoading,
  } = useDeleteVaults();

  const toggleIsFilterOpen = useCallback<NonNullable<IFilterButtonProps['onChange']>>(
    (event) => setIsFilterOpen(!isFilterOpen, event),
    [setIsFilterOpen, isFilterOpen],
  );
  const handleFilterClose = useCallback(
    () => setIsFilterOpen(false),
    [setIsFilterOpen],
  );
  const handleDetachSecureNotesModalOpen = useCallback(
    () => setIsDetachSecureNotesModalOpen(true),
    [],
  );
  const handleDetachSecureNotesModalClose = useCallback(
    () => setIsDetachSecureNotesModalOpen(false),
    [],
  );
  const handleDeleteSecureNotesModalOpen = useCallback(
    () => setIsDeleteSecureNotesModalOpen(true),
    [],
  );
  const handleDeleteSecureNotesModalClose = useCallback(
    () => setIsDeleteSecureNotesModalOpen(false),
    [],
  );
  const handleCreateSecureNoteModalOpen = useCallback(
    () => setIsCreateSecureNoteModalOpen(true),
    [],
  );
  const handleCreateSecureNoteModalClose = useCallback(
    () => setIsCreateSecureNoteModalOpen(false),
    [],
  );
  const handleMoveToPrivateSecureNotesModalOpen = useCallback(
    () => setIsMoveToPrivateSecureNotesModalOpen(true),
    [],
  );
  const handleMoveToPrivateSecureNotesModalClose = useCallback(
    () => setIsMoveToPrivateSecureNotesModalOpen(false),
    [],
  );
  const handleMoveToEmployeeSecureNotesModalOpen = useCallback(
    () => setIsMoveToEmployeeSecureNotesModalOpen(true),
    [],
  );
  const handleMoveToEmployeeSecureNotesModalClose = useCallback(
    () => setIsMoveToEmployeeSecureNotesModalOpen(false),
    [],
  );
  const handleShareSecureNotesModalOpen = useCallback(
    () => setIsShareSecureNotesModalOpen(true),
    [],
  );
  const handleShareSecureNotesModalClose = useCallback(
    () => setIsShareSecureNotesModalOpen(false),
    [],
  );
  const handleMoveToGroupSecureNotesModalOpen = useCallback(
    () => setIsMoveToGroupSecureNotesModalOpen(true),
    [],
  );
  const handleMoveToGroupSecureNotesModalClose = useCallback(
    () => setIsMoveToGroupSecureNotesModalOpen(false),
    [],
  );

  const handleSearchChange = useCallback<ISearchableTextFieldProps['onChange']>(
    (debouncedValue) => {
      setSearchQuery(debouncedValue);
      resetActivePage();
    },
    [setSearchQuery, resetActivePage],
  );

  const handleFilterSubmit = useCallback((
    updatedValues: IOrganizationSecureNotesTabFilterSubmitResult,
  ) => {
    setFilterValues(updatedValues);
    resetActivePage();
  }, [setFilterValues, resetActivePage]);

  const handleCreateSecureNote = useCallback(async (params: ICreateSecureNoteReturnValue) => {
    const { name, note } = params;
    setIsCreateSecureNoteLoading(true);
    try {
      await createVaultSecureNote({
        name,
        note: encryptSymmetric({
          key: symmetricKey,
          string: note,
          fallbackValue: null,
        }),
      });
      if (isTrustedPortalEnabled) {
        showSuccess({
          text: t('trustedPortalSuccessNotifications.secureNote.created'),
        });
      } else {
        showWarning({
          text: t('createSecureNoteModal.approvalOnMobileToast'),
        });
      }
      handleCreateSecureNoteModalClose();
    } catch (e) {
      showError({
        text: t('common.somethingWentWrong'),
      });
      logException(e, {
        message: 'OrganizationSecureNotesTab/handleCreateSecureNote exception',
      });
    } finally {
      setIsCreateSecureNoteLoading(false);
    }
  }, [
    createVaultSecureNote,
    handleCreateSecureNoteModalClose,
    symmetricKey,
    showError,
    showSuccess,
    showWarning,
    isTrustedPortalEnabled,
    t,
  ]);

  const handleDetachSecureNote = useCallback(async () => {
    mutateDetachSecureNote({
      vaultIds: selectedSecureNotesIds,
    }, {
      onSuccess: ({ failCount, successCount }) => {
        if (successCount) {
          showSuccess({
            text: t(
              'organizationSecureNotesTab.detachSecureNoteModal.successMessage',
              { count: successCount },
            ),
          });
        }
        if (failCount) {
          showError({
            text: t('organizationSecureNotesTab.detachSecureNoteModal.errorMessage', {
              count: failCount,
            }),
          });
        }
        handleDetachSecureNotesModalClose();
        resetSelectedRows();
        resetActivePage();
      },
      onError: (e) => {
        showError({ text: t(getTranslationKeyByError(e)) });
        logException(e, { message: 'OrganizationSecureNotesTab/handleDetachSecureNote exception' });
      },
    });
  }, [
    selectedSecureNotesIds,
    mutateDetachSecureNote,
    handleDetachSecureNotesModalClose,
    resetSelectedRows,
    resetActivePage,
    showSuccess,
    showError,
    t,
  ]);

  const handleDeleteSecureNote = useCallback(async () => {
    mutateDeleteSecureNote({
      vaultIds: selectedSecureNotesIds,
    }, {
      onSuccess: ({ failCount, successCount }) => {
        if (successCount) {
          showSuccess({
            text: t(
              'organizationSecureNotesTab.deleteSecureNoteModal.successMessage',
              { count: successCount },
            ),
          });
        }
        if (failCount) {
          showError({
            text: t(
              'organizationSecureNotesTab.deleteSecureNoteModal.errorMessage',
              { count: failCount },
            ),
          });
        }
        handleDeleteSecureNotesModalClose();
        resetSelectedRows();
        resetActivePage();
      },
      onError: (e) => {
        showError({ text: t(getTranslationKeyByError(e)) });
        logException(e, { message: 'OrganizationSecureNotesTab/handleDeleteSecureNote exception' });
      },
    });
  }, [
    selectedSecureNotesIds,
    mutateDeleteSecureNote,
    handleDeleteSecureNotesModalClose,
    resetSelectedRows,
    resetActivePage,
    showSuccess,
    showError,
    t,
  ]);

  const {
    isMoveToPrivateDisabled,
    isMoveToEmployeeDisabled,
    isMoveToGroupDisabled,
    isShareDisabled,
    isRemoveDisabled,
    isDeleteDisabled,
    moveToPrivateTooltipTitle,
    moveToEmployeeTooltipTitle,
    moveToGroupTooltipTitle,
    shareTooltipTitle,
    removeTooltipTitle,
    deleteTooltipTitle,
  } = useMemo(() => {
    if (!selectedSecureNotesAsObjects.length) {
      return {
        isMoveToPrivateDisabled: true,
        isMoveToEmployeeDisabled: true,
        isMoveToGroupDisabled: true,
        isShareDisabled: true,
        isRemoveDisabled: true,
        isDeleteDisabled: true,
        moveToPrivateTooltipTitle: t('common.tickRow'),
        moveToEmployeeTooltipTitle: t('common.tickRow'),
        moveToGroupTooltipTitle: t('common.tickRow'),
        shareTooltipTitle: t('common.tickRow'),
        removeTooltipTitle: t('common.tickRow'),
        deleteTooltipTitle: t('common.tickRow'),
      };
    }
    let disableShare = false;
    let disableRemove = false;
    let disableDelete = false;
    let disableMoveToPrivate = false;
    let moveToPrivateTitle = t('organizationSecureNotesTab.moveToPrivate.active');
    let shareTitle = t('organizationSecureNotesTab.shareToGroup.active');
    let removeTitle = t('organizationSecureNotesTab.remove.active');
    let deleteTitle = t('organizationSecureNotesTab.delete.active');
    selectedSecureNotesAsObjects.forEach(({ ownership }) => {
      if (ownership === Ownership.Unmanaged) {
        disableRemove = true;
        removeTitle = t('organizationSecureNotesTab.remove.disabled');
        disableShare = true;
        shareTitle = t('organizationSecureNotesTab.shareToGroup.disabled');
        disableMoveToPrivate = true;
        moveToPrivateTitle = t('organizationSecureNotesTab.moveToPrivate.disabled');
      }
      if (ownership === Ownership.Employees) {
        disableShare = true;
        shareTitle = t('organizationSecureNotesTab.shareToGroup.disabled');
        disableDelete = true;
        deleteTitle = t('organizationSecureNotesTab.delete.disabled');
      }
      if (ownership === Ownership.Groups) {
        disableDelete = true;
        deleteTitle = t('organizationSecureNotesTab.delete.disabled');
        disableMoveToPrivate = true;
        moveToPrivateTitle = t('organizationSecureNotesTab.moveToPrivate.disabled');
      }
    });
    const uniqueOwnershipCount = new Set(selectedSecureNotesAsObjects.map(
      ({ ownership }) => ownership,
    )).size;
    if (uniqueOwnershipCount > 1) {
      disableShare = true;
      disableRemove = true;
      disableDelete = true;
      disableMoveToPrivate = true;
      shareTitle = t('common.valuesInManagedByShouldBeIdentical');
      removeTitle = t('common.valuesInManagedByShouldBeIdentical');
      deleteTitle = t('common.valuesInManagedByShouldBeIdentical');
      moveToPrivateTitle = t('common.valuesInManagedByShouldBeIdentical');
    }
    return {
      isMoveToPrivateDisabled: disableMoveToPrivate,
      isMoveToEmployeeDisabled: false,
      isMoveToGroupDisabled: false,
      isShareDisabled: disableShare,
      isRemoveDisabled: disableRemove,
      isDeleteDisabled: disableDelete,
      moveToPrivateTooltipTitle: moveToPrivateTitle,
      moveToEmployeeTooltipTitle: t('organizationSecureNotesTab.moveToEmployee.active'),
      moveToGroupTooltipTitle: t('organizationSecureNotesTab.moveToGroup.active'),
      shareTooltipTitle: shareTitle,
      removeTooltipTitle: removeTitle,
      deleteTooltipTitle: deleteTitle,
    };
  }, [selectedSecureNotesAsObjects, t]);

  const ownership = useMemo(() => {
    if (selectedSecureNotesAsObjects.length) {
      return selectedSecureNotesAsObjects[0].ownership;
    }
    return null;
  }, [selectedSecureNotesAsObjects]);

  const handleMoveToPrivateSecureNotes = useCallback(async () => {
    try {
      setIsMoveToPrivateSecureNotesLoading(true);
      await moveVaultsToPrivateKeychain({ vaultIds: selectedSecureNotesIds });
      if (isTrustedPortalEnabled) {
        showSuccess({ text: t('trustedPortalSuccessNotifications.secureNote.moved') });
      } else {
        showWarning({ text: t('moveSecureNotesModal.approvalOnMobileToast') });
      }
      resetSelectedRows();
      handleMoveToPrivateSecureNotesModalClose();
    } catch (e) {
      showError({ text: t(getTranslationKeyByError(e)) });
      logException(e, {
        message: 'OrganizationSecureNotesTab/handleMoveToPrivateSecureNotes exception',
      });
    } finally {
      setIsMoveToPrivateSecureNotesLoading(false);
    }
  }, [
    moveVaultsToPrivateKeychain,
    selectedSecureNotesIds,
    isTrustedPortalEnabled,
    showWarning,
    showError,
    showSuccess,
    handleMoveToPrivateSecureNotesModalClose,
    resetSelectedRows,
    t,
  ]);

  const moveToPrivateSecureNotesModalDescriptionElement = useMemo(
    () => (
      <>
        <Typography>{t('moveSecureNotesToPrivateKeychainModal.description')}</Typography>
        <Typography>{t('moveSecureNotesToPrivateKeychainModal.additionalDescription')}</Typography>
      </>
    ),
    [t],
  );

  return (
    <PanelContent p={0}>
      <Grid container justifyContent="space-between" alignItems="stretch" p={1}>
        <Grid item xs={4} container flexWrap="nowrap" spacing={1}>
          <Grid item>
            <Tooltip title={t('common.filter')}>
              <FilterButton
                isFilterActive={isFilterActive}
                numberOfActiveFilters={numberOfActiveFilters}
                selected={isFilterOpen}
                onChange={toggleIsFilterOpen}
              />
            </Tooltip>
          </Grid>
          <Grid item my={0.5}>
            <Divider orientation="vertical" />
          </Grid>
          <Grid item alignSelf="center">
            <Tooltip title={moveToPrivateTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleMoveToPrivateSecureNotesModalOpen}
                disabled={isMoveToPrivateDisabled}
                variant={SeverityEnum.Error}
              >
                <MoveIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
          <Grid item alignSelf="center">
            <Tooltip title={moveToEmployeeTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleMoveToEmployeeSecureNotesModalOpen}
                disabled={isMoveToEmployeeDisabled}
              >
                <MoveToEmployeeIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
          <Grid item alignSelf="center">
            <Tooltip title={moveToGroupTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleMoveToGroupSecureNotesModalOpen}
                disabled={isMoveToGroupDisabled}
              >
                <MoveToGroupIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
          <Grid item alignSelf="center">
            <Tooltip title={shareTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleShareSecureNotesModalOpen}
                disabled={isShareDisabled}
              >
                <ShareIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
          <Grid item alignSelf="center">
            <Tooltip title={removeTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleDetachSecureNotesModalOpen}
                disabled={isRemoveDisabled}
                variant={SeverityEnum.Error}
              >
                <ErrorIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
          <Grid item alignSelf="center">
            <Tooltip title={deleteTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleDeleteSecureNotesModalOpen}
                disabled={isDeleteDisabled}
                variant={SeverityEnum.Error}
              >
                <DeleteIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
        </Grid>
        <Grid item xs={8} container justifyContent="flex-end" flexWrap="nowrap">
          <Grid item>
            <SearchableTextField
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={t('common.search')}
            />
          </Grid>
          <Box mr={3} />
          <Grid item>
            <Button
              icon={<PlusIcon />}
              onClick={handleCreateSecureNoteModalOpen}
            >
              {t('secureNotesTab.createSecureNoteButton')}
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Divider />
      <OrganizationSecureNotesTable
        selectedSecureNotes={selectedSecureNotes}
        {...restTableProps}
      />
      {isCreateSecureNoteModalOpen && (
        <CreateSecureNoteModal
          isOpen={isCreateSecureNoteModalOpen}
          onClose={handleCreateSecureNoteModalClose}
          onSubmit={handleCreateSecureNote}
          isLoading={isCreateSecureNoteLoading}
        />
      )}
      <OrganizationSecureNotesTabFilter
        isOpen={isFilterOpen}
        anchorEl={filterAnchorEl}
        onSubmit={handleFilterSubmit}
        onClose={handleFilterClose}
        initialValues={filterValues}
      />
      {isMoveToPrivateSecureNotesModalOpen && (
        <PromptModal
          open={isMoveToPrivateSecureNotesModalOpen}
          onClose={handleMoveToPrivateSecureNotesModalClose}
          onSubmit={handleMoveToPrivateSecureNotes}
          title={t(
            'moveSecureNotesToPrivateKeychainModal.title',
            { count: selectedSecureNotes.size },
          )}
          description={moveToPrivateSecureNotesModalDescriptionElement}
          list={selectedSecureNotesAsObjects}
          renderField="name"
          renderKey="vaultId"
          isLoading={isMoveToPrivateSecureNotesLoading}
          approvalButtonText="common.move"
          approvalButtonColor="primary"
        />
      )}
      {isMoveToEmployeeSecureNotesModalOpen && (
        <MoveVaultsToEmployeeModal
          isOpen={isMoveToEmployeeSecureNotesModalOpen}
          onClose={handleMoveToEmployeeSecureNotesModalClose}
          vaults={selectedSecureNotesAsObjects}
          onSubmit={resetSelectedRows}
          type={VaultTypeEnum.SecureNote}
        />
      )}
      {isMoveToGroupSecureNotesModalOpen && (
        <MoveVaultsToGroupModal
          isOpen={isMoveToGroupSecureNotesModalOpen}
          onClose={handleMoveToGroupSecureNotesModalClose}
          vaults={selectedSecureNotesAsObjects}
          onSubmit={resetSelectedRows}
          type={VaultTypeEnum.SecureNote}
        />
      )}
      {isShareSecureNotesModalOpen && (
        <ShareVaultsToGroupModal
          isOpen={isShareSecureNotesModalOpen}
          onClose={handleShareSecureNotesModalClose}
          vaults={selectedSecureNotesAsObjects}
          type={VaultTypeEnum.SecureNote}
          onSubmit={resetSelectedRows}
        />
      )}
      {isDetachSecureNotesModalOpen && (
        <PromptModal
          open={isDetachSecureNotesModalOpen}
          onClose={handleDetachSecureNotesModalClose}
          onSubmit={handleDetachSecureNote}
          title={t(
            `organizationSecureNotesTab.detachSecureNoteModal.detachFrom${ownership}.title`,
            { count: selectedSecureNotes.size },
          )}
          description={t(
            `organizationSecureNotesTab.detachSecureNoteModal.detachFrom${ownership}.description`,
          )}
          additionalDescription={t(
            // eslint-disable-next-line max-len
            `organizationSecureNotesTab.detachSecureNoteModal.detachFrom${ownership}.additionalDescription`,
          )}
          list={selectedSecureNotesAsObjects}
          renderField="name"
          renderKey="vaultId"
          isLoading={isDetachSecureNotesLoading}
        />
      )}
      {isDeleteSecureNotesModalOpen && (
        <PromptModal
          open={isDeleteSecureNotesModalOpen}
          onClose={handleDeleteSecureNotesModalClose}
          onSubmit={handleDeleteSecureNote}
          title={t(
            'organizationSecureNotesTab.deleteSecureNoteModal.title',
            { count: selectedSecureNotes.size },
          )}
          description={t(
            'organizationSecureNotesTab.deleteSecureNoteModal.description',
          )}
          approvalButtonText="common.delete"
          list={selectedSecureNotesAsObjects}
          renderField="name"
          renderKey="vaultId"
          isLoading={isDeleteSecureNotesLoading}
        />
      )}
    </PanelContent>
  );
};

export default OrganizationSecureNotesTab;
